import { DRAGON } from "../config.ts";
import { TAU, damp, lerp } from "../core/vec.ts";
import type { SpawnContext } from "../content/levelTypes.ts";

interface Segment {
  x: number;
  y: number;
  px: number;
  py: number;
  angle: number;
  r: number;
}

/**
 * PYRE — the final boss. A serpentine dragon built from a head plus a chain of
 * follow-the-leader segments; every segment is lethal, so the body itself is a
 * moving wall the player reads. The dragon escalates through three phases
 * driven by the level timeline and departs when time runs out — but it also
 * has HP now: a sustained sword offense can fell it early (DRAGON.hp hits),
 * ending the fight in a death sequence instead of a timeout.
 *
 *   Phase 1 — lazy figure-eight cruise; aimed fireball spreads; roar rings.
 *   Phase 2 — faster; sweeping fire-breath cones; telegraphed dive attacks.
 *   Phase 3 — tighter cadence on everything at once.
 *   Phase 4 — the FINAL FRENZY: every attack at maximum rate, plus two
 *             frenzy-only moves — a rotating spiral barrage from the head and
 *             a burning wake of lingering embers shed from the tail.
 *
 * The dragon deliberately holds the upper half of the arena while cruising so
 * the player always has room to work — pressure comes from projectiles and
 * dives, not corner-camping.
 */
export class Dragon {
  active = false;
  leaving = false;
  phase = 1;

  x = 0;
  y = 0;
  px = 0;
  py = 0;
  angle = 0;
  readonly segs: Segment[] = [];

  hp = DRAGON.hp;
  maxHp = DRAGON.hp;
  hitFlash = 0; // white flash timer after taking a sword hit
  dying = 0; // death-animation countdown; > 0 means no attacks, no collision

  /** Render hooks: mouth-flame intensity and dive-telegraph progress [0..1]. */
  fireGlow = 0;
  telegraph = 0;
  /** Set true for one frame when the dragon roars (consumed by World → audio). */
  justRoared = false;

  private pathT = 0;
  private fireT = 2;
  private roarT = 5;
  private breathT = 8; // countdown to next breath (phase 2+)
  private breathing = 0; // remaining breath duration
  private breathTick = 0;
  private diveT = 9; // countdown to next dive (phase 2+)
  // Dive state machine: 0 = cruising, 1 = winding up, 2 = diving.
  private diveState = 0;
  private diveDirX = 0;
  private diveDirY = 0;
  private windup = 0;
  // Frenzy-only (phase 4) attack state.
  private spiralT = 0; // countdown to next spiral shot
  private spiralA = 0; // rotating spiral emit angle
  private wakeT = 0; // countdown to next tail ember
  // Final attack: 0 = off, 1 = climbing to the top perch, 2 = FIRE BLAST.
  private finaleMode = 0;
  private finaleTick = 0;

  constructor() {
    for (let i = 0; i < DRAGON.segments; i++) {
      const t = i / (DRAGON.segments - 1);
      this.segs.push({ x: 0, y: 0, px: 0, py: 0, angle: 0, r: lerp(DRAGON.headRadius * 0.85, DRAGON.tailRadius, t) });
    }
  }

  /** Fly in from above the arena and start phase 1. */
  enter(arenaW: number): void {
    this.active = true;
    this.leaving = false;
    this.phase = 1;
    this.hp = this.maxHp = DRAGON.hp;
    this.hitFlash = 0;
    this.dying = 0;
    this.pathT = 0;
    this.fireT = 2.5;
    this.roarT = 1.2; // announce arrival with a roar
    this.breathT = 8;
    this.breathing = 0;
    this.diveT = 9;
    this.diveState = 0;
    this.spiralT = 0;
    this.spiralA = 0;
    this.wakeT = 0;
    this.finaleMode = 0;
    this.finaleTick = 0;
    this.fireGlow = 0;
    this.telegraph = 0;
    this.x = this.px = arenaW / 2;
    this.y = this.py = -200;
    this.angle = Math.PI / 2;
    for (const s of this.segs) {
      s.x = s.px = this.x;
      s.y = s.py = this.y - DRAGON.spacing;
    }
  }

  setPhase(p: number): void {
    this.phase = p;
    this.roarT = 0.4; // punctuate each escalation with a roar
  }

  /** Break off the fight and fly out the top. */
  leave(): void {
    this.leaving = true;
    this.diveState = 0;
    this.breathing = 0;
    this.telegraph = 0;
  }

  /** The FINAL ATTACK: break off everything, climb to the top of the screen,
   * and unleash a screen-wide fire blast raining down until the detonation. */
  beginFinale(): void {
    this.finaleMode = 1;
    this.diveState = 0;
    this.breathing = 0;
    this.telegraph = 0;
    this.roarT = 0.15; // announce the last stand
    this.justRoared = false;
  }

  update(dt: number, ctx: SpawnContext): void {
    if (!this.active) return;
    this.px = this.x;
    this.py = this.y;
    for (const s of this.segs) {
      s.px = s.x;
      s.py = s.y;
    }

    if (this.hitFlash > 0) this.hitFlash -= dt;
    this.fireGlow = Math.max(0, this.fireGlow - dt * 3);

    if (this.leaving) {
      // Climb out of the arena, then deactivate.
      this.y -= 700 * dt;
      this.angle = damp(this.angle, -Math.PI / 2, 4, dt);
      this.followSegments();
      if (this.y < -400) this.active = false;
      return;
    }

    if (this.finaleMode > 0) {
      this.updateFinale(dt, ctx);
      this.followSegments();
      return;
    }

    this.moveHead(dt, ctx);
    this.followSegments();
    this.attacks(dt, ctx);
  }

  /** The final attack: climb to a perch at the top-center, face down, and
   * pour fire over the whole screen until the scripted detonation. */
  private updateFinale(dt: number, ctx: SpawnContext): void {
    const perchX = ctx.arenaW / 2;
    const perchY = ctx.arenaH * 0.12;
    if (this.finaleMode === 1) {
      // Climb: fly directly to the perch, no attacks — a readable beat of
      // calm before the storm.
      const dx = perchX - this.x;
      const dy = perchY - this.y;
      const d = Math.hypot(dx, dy);
      const sp = Math.min(680, d * 4);
      if (d > 1) {
        this.x += (dx / d) * sp * dt;
        this.y += (dy / d) * sp * dt;
        this.angle = damp(this.angle, Math.atan2(dy, dx), 6, dt);
      }
      if (d < 26) {
        this.finaleMode = 2;
        this.finaleTick = 0;
        this.roarT = 0; // roar the moment the blast opens
      }
      return;
    }
    // Blasting: hover on the perch facing straight down, torching the arena.
    this.x = damp(this.x, perchX, 4, dt);
    this.y = perchY + Math.sin(this.pathT * 3) * 8;
    this.pathT += dt;
    this.angle = damp(this.angle, Math.PI / 2, 6, dt);
    this.fireGlow = 1;

    this.roarT -= dt;
    if (this.roarT <= 0) {
      this.roarT = 2.4;
      this.justRoared = true;
    }

    this.finaleTick -= dt;
    if (this.finaleTick <= 0) {
      this.finaleTick = 0.055;
      // One shot from the mouth fanned loosely toward the player…
      const mouthX = this.x + Math.cos(this.angle) * DRAGON.headRadius;
      const mouthY = this.y + Math.sin(this.angle) * DRAGON.headRadius;
      const aim = Math.atan2(ctx.playerY - mouthY, ctx.playerX - mouthX);
      const a = aim + ctx.rng.range(-0.5, 0.5);
      ctx.bullet(mouthX, mouthY, Math.cos(a) * 430, Math.sin(a) * 430, 11, {
        color: 0xff7a3c,
      });
      // …and one raining straight down from a random point across the full
      // screen width, so the blast covers everything.
      const rx = ctx.rng.range(0.04, 0.96) * ctx.arenaW;
      ctx.bullet(rx, this.y + ctx.rng.range(-30, 30), ctx.rng.range(-25, 25), ctx.rng.range(400, 520), 9, {
        color: 0xffa136,
      });
    }
  }

  // --- Movement --------------------------------------------------------------
  private moveHead(dt: number, ctx: SpawnContext): void {
    const speedMul = this.phase === 1 ? 1 : this.phase === 2 ? 1.25 : this.phase === 3 ? 1.5 : 1.75;

    if (this.diveState === 1) {
      // Windup: hover, glow, face the player. The pause IS the telegraph.
      this.windup -= dt;
      this.telegraph = 1 - Math.max(0, this.windup) / 0.8;
      const a = Math.atan2(ctx.playerY - this.y, ctx.playerX - this.x);
      this.angle = damp(this.angle, a, 10, dt);
      if (this.windup <= 0) {
        this.diveDirX = Math.cos(this.angle);
        this.diveDirY = Math.sin(this.angle);
        this.diveState = 2;
        this.telegraph = 0;
      }
      return;
    }

    if (this.diveState === 2) {
      // Straight lethal dash through the player's marked position.
      this.x += this.diveDirX * DRAGON.diveSpeed * dt;
      this.y += this.diveDirY * DRAGON.diveSpeed * dt;
      const m = 220;
      if (
        this.x < -m || this.x > ctx.arenaW + m ||
        this.y < -m || this.y > ctx.arenaH + m
      ) {
        this.diveState = 0; // exited the field; cruise path will reel it back in
      }
      return;
    }

    // Cruise: chase a Lissajous target that owns the arena's upper half.
    this.pathT += dt * speedMul;
    const tx = ctx.arenaW / 2 + Math.sin(this.pathT * 0.7) * ctx.arenaW * 0.34;
    const ty = ctx.arenaH * 0.28 + Math.sin(this.pathT * 1.3) * ctx.arenaH * 0.16;
    const dx = tx - this.x;
    const dy = ty - this.y;
    const d = Math.hypot(dx, dy);
    if (d > 1) {
      const sp = Math.min(DRAGON.cruiseSpeed * speedMul, d * 3);
      this.x += (dx / d) * sp * dt;
      this.y += (dy / d) * sp * dt;
      const want = Math.atan2(dy, dx);
      // Shortest-arc angular damp so the head banks smoothly through turns.
      let diff = want - this.angle;
      while (diff > Math.PI) diff -= TAU;
      while (diff < -Math.PI) diff += TAU;
      this.angle += diff * Math.min(1, 6 * dt);
    }
  }

  private followSegments(): void {
    let leadX = this.x;
    let leadY = this.y;
    for (const s of this.segs) {
      const dx = leadX - s.x;
      const dy = leadY - s.y;
      const d = Math.hypot(dx, dy);
      if (d > DRAGON.spacing) {
        const pull = (d - DRAGON.spacing) / d;
        s.x += dx * pull;
        s.y += dy * pull;
      }
      s.angle = Math.atan2(leadY - s.y, leadX - s.x);
      leadX = s.x;
      leadY = s.y;
    }
  }

  // --- Attacks ---------------------------------------------------------------
  private attacks(dt: number, ctx: SpawnContext): void {
    const p = this.phase;

    // Roar: a ring of embers from the head, on a slow clock.
    this.roarT -= dt;
    if (this.roarT <= 0) {
      this.roarT = p === 1 ? 7 : p === 2 ? 6 : p === 3 ? 5 : 3.5;
      this.justRoared = true;
      this.fireGlow = 1;
      const n = p >= 4 ? 26 : p === 3 ? 22 : 16;
      for (let i = 0; i < n; i++) {
        const a = this.angle + (i / n) * TAU;
        ctx.bullet(this.x, this.y, Math.cos(a) * 210, Math.sin(a) * 210, 9, {
          color: 0xffa136,
        });
      }
    }

    // Aimed fireball spread from the mouth.
    this.fireT -= dt;
    if (this.fireT <= 0 && this.diveState !== 2) {
      this.fireT = p === 1 ? 1.7 : p === 2 ? 1.3 : p === 3 ? 0.95 : 0.7;
      this.fireGlow = 1;
      const mouthX = this.x + Math.cos(this.angle) * DRAGON.headRadius;
      const mouthY = this.y + Math.sin(this.angle) * DRAGON.headRadius;
      const base = Math.atan2(ctx.playerY - mouthY, ctx.playerX - mouthX);
      const shots = p >= 4 ? 5 : p === 3 ? 4 : 3;
      for (let i = 0; i < shots; i++) {
        const a = base + (i / (shots - 1) - 0.5) * 0.5;
        ctx.bullet(mouthX, mouthY, Math.cos(a) * 350, Math.sin(a) * 350, 12, {
          color: 0xff7a3c,
        });
      }
    }

    // FRENZY-ONLY: two attacks unveiled for the final stand.
    if (p >= 4) {
      // Spiral barrage: a continuous rotating spray from the head; the arms
      // precess so there's always a moving gap to ride.
      this.spiralT -= dt;
      if (this.spiralT <= 0 && this.diveState !== 2) {
        this.spiralT = 0.09;
        this.spiralA += 0.47;
        ctx.bullet(
          this.x,
          this.y,
          Math.cos(this.spiralA) * 260,
          Math.sin(this.spiralA) * 260,
          8,
          { color: 0xffd166 },
        );
      }
      // Burning wake: the tail sheds slow, lingering embers — chasing the
      // dragon's rear is no longer safe.
      this.wakeT -= dt;
      if (this.wakeT <= 0) {
        this.wakeT = 0.35;
        const tail = this.segs[this.segs.length - 1];
        ctx.bullet(
          tail.x,
          tail.y,
          ctx.rng.range(-35, 35),
          ctx.rng.range(-35, 35),
          9,
          { color: 0xff7a3c, life: 2.6, cullOffscreen: false },
        );
      }
    }

    if (p >= 2) {
      // Fire breath: a sweeping cone of embers held for ~1.6s.
      if (this.breathing > 0) {
        this.breathing -= dt;
        this.breathTick -= dt;
        this.fireGlow = 1;
        if (this.breathTick <= 0) {
          this.breathTick = 0.07;
          const mouthX = this.x + Math.cos(this.angle) * DRAGON.headRadius;
          const mouthY = this.y + Math.sin(this.angle) * DRAGON.headRadius;
          const base = Math.atan2(ctx.playerY - mouthY, ctx.playerX - mouthX);
          const sweep = Math.sin(this.breathing * 7) * 0.45;
          const a = base + sweep + ctx.rng.range(-0.06, 0.06);
          const sp = ctx.rng.range(380, 480);
          ctx.bullet(mouthX, mouthY, Math.cos(a) * sp, Math.sin(a) * sp, 8, {
            color: 0xffa136,
          });
        }
      } else {
        this.breathT -= dt;
        if (this.breathT <= 0 && this.diveState === 0) {
          this.breathT = p === 2 ? 5.5 : p === 3 ? 4.2 : 3;
          this.breathing = 1.6;
          this.breathTick = 0;
        }
      }

      // Dive attack: wind up (telegraph), then dash through the player.
      if (this.diveState === 0 && this.breathing <= 0) {
        this.diveT -= dt;
        if (this.diveT <= 0) {
          this.diveT = p === 2 ? 8 : p === 3 ? 6 : 4;
          this.diveState = 1;
          this.windup = 0.8;
          this.justRoared = true;
        }
      }
    }
  }
}
